import React, { useEffect, useState } from 'react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../lib/googleMaps';
import AdvancedMarker from './maps/AdvancedMarker';
import api from '../lib/api';

/**
 * Renders the actual GPS-recorded route for a completed trip.
 *
 * - `tripId` (required) → /api/trips/detail/{id} provides `gps_tracks`
 * - `height` (optional) → CSS height, default 200px
 * - `compact` (optional) → trims chrome for tight cards
 */
export default function TripRouteMap({ tripId, height = '200px', compact = false }) {
  const { isLoaded } = useGoogleMaps();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!tripId) return undefined;
    api.get(`/trips/detail/${tripId}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tripId]);

  if (!isLoaded) {
    return <div style={{ height }} className="bg-[#F3F3F3] flex items-center justify-center text-[10px] text-[#666] uppercase tracking-widest rounded-xl">Loading map…</div>;
  }
  if (!data) return null;

  const tracks = (data.gps_tracks || []).filter(t => t && t.lat && t.lng);
  if (tracks.length === 0) {
    return (
      <div style={{ height }} className="bg-[#F3F3F3] flex items-center justify-center text-[10px] text-[#666] uppercase tracking-widest rounded-xl">
        No GPS points recorded
      </div>
    );
  }

  const path = tracks.map(t => ({ lat: t.lat, lng: t.lng }));
  const mid = path[Math.floor(path.length / 2)];
  const trip = data.trip || {};
  const pickup = trip.pickup_lat && trip.pickup_lng ? { lat: trip.pickup_lat, lng: trip.pickup_lng } : null;
  const drop = trip.drop_lat && trip.drop_lng ? { lat: trip.drop_lat, lng: trip.drop_lng } : null;

  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid={`trip-route-map-${tripId}`}>
      {!compact && (
        <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold p-2 bg-[#F8F8F8] flex items-center justify-between">
          <span>Travelled Route</span>
          <span className="text-[#002FA7] font-mono">
            {data.google_kms ? `${data.google_kms} km` : ''}{tracks.length ? ` · ${tracks.length} pts` : ''}
          </span>
        </p>
      )}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height }}
        center={mid}
        zoom={12}
        options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative', mapId: GOOGLE_MAPS_MAP_ID }}
      >
        <Polyline path={path} options={{ strokeColor: '#002FA7', strokeWeight: 4, strokeOpacity: 0.85 }} />
        {pickup && <AdvancedMarker position={pickup} icon={{ color: '#00C853', label: 'P' }} />}
        {drop && <AdvancedMarker position={drop} icon={{ color: '#FF3333', label: 'D' }} />}
        <AdvancedMarker position={path[0]} icon={{ color: '#002FA7', label: 'S' }} title="Start" />
        <AdvancedMarker position={path[path.length - 1]} icon={{ color: '#0A0A0A', label: 'E' }} title="End" />
      </GoogleMap>
    </div>
  );
}
